import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toBlob as domToImageBlob } from "html-to-image";
import { apiUrl } from "@/api/client";
import { templateKeys } from "@/api/templates";
import { resolveDurationFrames } from "@/lib/durationResolve";
import { useAuthStore } from "@/stores/auth";
import { CardHoverPlayer } from "./CardHoverPlayer";
import type { FieldDefinition, Template } from "@/types/template";

interface ThumbnailWarmerProps {
  templates: Template[] | undefined;
}

/**
 * Background warmer: walks owned templates that don't yet have a thumbnail
 * on disk and generates one for each. Sequential (concurrency=1) so memory
 * doesn't spike from 12+ Remotion Players mounting at once.
 *
 * Each item is rendered into a hidden, offscreen div for ~2.5s — enough
 * time for the Player to compile its TSX, seek to ~30% of the duration,
 * and paint the frame. Then html-to-image snapshots the container, the
 * PNG is POSTed to /api/templates/:id/thumbnail, and the queue advances.
 *
 * The hidden container uses position:fixed + opacity:0.01 + pointer-events:
 * none. Opacity 0 (or display:none) would skip layout/paint, leaving the
 * Player blank. 0.01 is invisible to the user but still rendered.
 *
 * sessionStorage flags prevent retry storms on failed captures within a
 * single tab session. Reload the page to retry.
 */
export function ThumbnailWarmer({ templates }: ThumbnailWarmerProps) {
  const currentUser = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<Template[]>([]);
  const [current, setCurrent] = useState<Template | null>(null);
  const captureNodeRef = useRef<HTMLDivElement>(null);
  // Tracks whether the queue has already been seeded for this list of
  // templates. Without this guard, every templates-query refetch (e.g.
  // after a thumbnail upload re-invalidates the cache) would re-seed
  // the queue and keep us churning forever.
  const seededRef = useRef<string>("");

  // Build the queue once per (user, templates-list) tuple. Probe each
  // candidate's thumbnail.png with HEAD; only queue 404s. Already-attempted
  // templates (sessionStorage flag) are skipped so a single failing
  // template doesn't block the rest behind it on every render.
  useEffect(() => {
    if (!currentUser || !templates) return;
    // SVG templates render their card image from source — they have no PNG
    // thumbnail and would fail the Remotion /preview/compile step, so exclude
    // them from the warm queue entirely.
    const sig = templates
      .filter((t) => t.user_id === currentUser.id && t.project_kind !== "svg")
      .map((t) => t.id)
      .join(",");
    if (!sig || seededRef.current === sig) return;
    seededRef.current = sig;

    let cancelled = false;
    (async () => {
      const owned = templates.filter(
        (t) => t.user_id === currentUser.id && t.project_kind !== "svg",
      );
      const needs: Template[] = [];
      for (const t of owned) {
        if (sessionStorage.getItem(`mf-thumb-warmed:${t.id}`)) continue;
        try {
          const res = await fetch(apiUrl(`/templates/${t.id}/thumbnail.png`), {
            method: "HEAD",
          });
          if (res.ok) {
            // Already has a thumbnail; mark warmed so we don't probe again.
            sessionStorage.setItem(`mf-thumb-warmed:${t.id}`, "exists");
            continue;
          }
          needs.push(t);
        } catch {
          // Probe failed — skip silently and let the user manually capture.
        }
        if (cancelled) return;
      }
      if (!cancelled && needs.length > 0) {
        setQueue(needs);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templates, currentUser]);

  // Pull the next template off the queue when we're idle.
  useEffect(() => {
    if (current || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setCurrent(next!);
  }, [queue, current]);

  // Capture pipeline for the current template. Triggered when `current`
  // becomes non-null. Mounts CardHoverPlayer (which compiles the TSX +
  // mounts a Remotion Player playing from frame 0), waits for it to
  // settle, then runs html-to-image on the container.
  useEffect(() => {
    if (!current) return;
    const t = current;
    let cancelled = false;
    const token = useAuthStore.getState().token;

    const run = async () => {
      // Give the Player ~2s to compile + render the first ~30% of frames
      // at default playback speed. CardHoverPlayer auto-plays + loops, so
      // by the time we capture the playhead is mid-animation.
      await new Promise((r) => setTimeout(r, 2200));
      if (cancelled) return;

      const node = captureNodeRef.current;
      if (!node) {
        sessionStorage.setItem(`mf-thumb-warmed:${t.id}`, "no-node");
        setCurrent(null);
        return;
      }

      try {
        const blob = await domToImageBlob(node, {
          canvasWidth: 1280,
          canvasHeight: 720,
          cacheBust: true,
        });
        if (!blob) throw new Error("empty blob");

        const res = await fetch(apiUrl(`/templates/${t.id}/thumbnail`), {
          method: "POST",
          headers: {
            "Content-Type": "image/png",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: blob,
        });
        if (!res.ok) throw new Error(`upload ${res.status}`);

        sessionStorage.setItem(`mf-thumb-warmed:${t.id}`, "uploaded");
        // Refresh the cards so the new thumbnail appears without a manual
        // reload. invalidateQueries instead of refetchQueries because
        // refetch can race with the in-flight HEAD probe of the next item
        // in the queue and cause flicker.
        queryClient.invalidateQueries({ queryKey: templateKeys.all });
      } catch (e) {
        console.warn("[thumbnail-warmer] failed:", t.id, e);
        sessionStorage.setItem(`mf-thumb-warmed:${t.id}`, "failed");
      } finally {
        if (!cancelled) setCurrent(null);
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [current, queryClient]);

  // The hidden render container. Always rendered in the DOM so the Player
  // it contains has a stable parent that won't reflow on every queue tick.
  // current === null → renders empty wrapper, no Player mounted, no work.
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        left: -9999,
        top: 0,
        width: 1280,
        height: 720,
        pointerEvents: "none",
        opacity: 0.01,
        zIndex: -1,
      }}
    >
      {current && (
        <div
          ref={captureNodeRef}
          style={{ width: 1280, height: 720 }}
          // key prop forces React to remount CardHoverPlayer for each
          // queue item — otherwise the inner useDynamicComponent + Player
          // hold onto stale module URLs.
          key={current.id}
        >
          <WarmerPlayerSlot template={current} />
        </div>
      )}
    </div>
  );
}

/**
 * Tiny wrapper so we can compute width/height/duration once per template.
 * The CardHoverPlayer is configured to auto-play and loop, which keeps
 * frames advancing the entire 2.2s window — so the html-to-image snapshot
 * lands somewhere in the middle of the composition rather than at frame 0.
 */
function WarmerPlayerSlot({ template }: { template: Template }) {
  // Compute these inline just so future maintainers can see the shape
  // matches CardHoverPlayer's own derivations. Not strictly needed since
  // CardHoverPlayer handles the math itself.
  const _ = useDerivedDuration(template);
  void _;
  return <CardHoverPlayer template={template} />;
}

function useDerivedDuration(template: Template) {
  let schema: FieldDefinition[] = [];
  if (Array.isArray(template.field_schema)) {
    schema = template.field_schema;
  } else if (typeof template.field_schema === "string") {
    try {
      schema = JSON.parse(template.field_schema) as FieldDefinition[];
    } catch {
      schema = [];
    }
  }
  const inputProps: Record<string, unknown> = {};
  for (const f of schema) inputProps[f.key] = f.default;
  const fps =
    typeof inputProps.fps === "number" && (inputProps.fps as number) > 0
      ? (inputProps.fps as number)
      : template.fps || 30;
  return resolveDurationFrames(inputProps, fps, template.duration_frames, schema);
}
